import type { Accommodation, AttributeDef, AttributeValue } from './api';

/**
 * Filtering, over attributes the park declared rather than a fixed list.
 *
 * The API returns `attributes` as definitions with a `kind`, and the reason
 * they are typed at all is stated in `app.ts`: an attribute nobody defined is
 * dropped, so a typo in an import cannot become a facet on the website. This
 * file is the other end of that — each kind gets the control it deserves, and a
 * kind nobody has taught it about is skipped rather than rendered as a text box
 * that filters on nothing.
 *
 * The whole thing is pure and synchronous, which is the point. Availability
 * across the filtered set is recomputed from held data by `combinePeriods`, so
 * changing a filter costs one render and zero requests.
 */

export interface NumberFilter { kind: 'number'; min?: number; max?: number }
export interface BooleanFilter { kind: 'boolean'; value: boolean }
export interface EnumFilter { kind: 'enum'; values: string[] }
export interface TextFilter { kind: 'text'; contains: string }

export type Filter = NumberFilter | BooleanFilter | EnumFilter | TextFilter;

/** Keyed by attribute id. An absent key is "no opinion". */
export type FilterState = Record<string, Filter>;

/** Filters a guest has actually expressed, as opposed to controls they touched. */
export function activeFilters(state: FilterState): string[] {
  return Object.entries(state).filter(([, f]) => isActive(f)).map(([id]) => id);
}

function isActive(f: Filter): boolean {
  switch (f.kind) {
    case 'number': return f.min !== undefined || f.max !== undefined;
    case 'boolean': return f.value;
    case 'enum': return f.values.length > 0;
    case 'text': return f.contains.trim().length > 0;
    default: return false;
  }
}

function matches(f: Filter, value: AttributeValue | undefined): boolean {
  switch (f.kind) {
    case 'number': {
      // A missing value fails a numeric filter rather than passing it. "At
      // least 40 m²" must not offer a pitch whose area the park never entered:
      // the guest asked a question and the honest answer is "we do not know",
      // which is not a yes.
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(n)) return false;
      if (f.min !== undefined && n < f.min) return false;
      if (f.max !== undefined && n > f.max) return false;
      return true;
    }
    case 'boolean':
      // One-sided on purpose. "Only with a private bathroom" is a real request;
      // "only WITHOUT a private bathroom" is not something anyone asks, and
      // offering it as a third state doubles the control for nobody.
      return value === true;
    case 'enum':
      return typeof value === 'string' && f.values.includes(value);
    case 'text':
      return typeof value === 'string'
        && value.toLowerCase().includes(f.contains.trim().toLowerCase());
    default:
      return true;
  }
}

export function applyFilters(
  accommodations: readonly Accommodation[], state: FilterState,
): Accommodation[] {
  const active = Object.entries(state).filter(([, f]) => isActive(f));
  if (active.length === 0) return [...accommodations];
  return accommodations.filter((a) => active.every(([id, f]) => matches(f, a.attributes[id])));
}

/**
 * The range a numeric attribute actually spans across the park's own stock.
 *
 * Used to place the inputs' bounds, so "at least" cannot be dragged past the
 * largest pitch that exists and return nothing with no explanation.
 */
export function numericRange(
  accommodations: readonly Accommodation[], id: string,
): { min: number; max: number } | null {
  let min = Infinity;
  let max = -Infinity;
  for (const a of accommodations) {
    const v = a.attributes[id];
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n)) continue;
    if (n < min) min = n;
    if (n > max) max = n;
  }
  return min <= max ? { min, max } : null;
}

/** The options an enum attribute actually uses, in the order the park declared. */
export function enumOptions(
  accommodations: readonly Accommodation[], def: AttributeDef,
): string[] {
  const used = new Set<string>();
  for (const a of accommodations) {
    const v = a.attributes[def.id];
    if (typeof v === 'string' && v) used.add(v);
  }
  const declared = def.options ?? [];
  // Declared order first — it is the operator's, and alphabetising a list they
  // arranged is a small act of vandalism. Anything in the data but not in the
  // definition follows, so a value added ahead of its definition still filters.
  return [
    ...declared.filter((o) => used.has(o)),
    ...[...used].filter((o) => !declared.includes(o)).sort(),
  ];
}

/** Attributes worth putting a control on the screen for. */
export function filterableDefs(
  defs: readonly AttributeDef[], accommodations: readonly Accommodation[],
): AttributeDef[] {
  return defs
    .filter((d) => d.filterable !== false)
    .filter((d) => {
      // A facet that cannot narrow anything costs a guest a decision and
      // returns nothing for it, so it does not get a control.
      if (d.kind === 'boolean') {
        // "Only with this" is worth offering exactly when some have it and some
        // do not — including the case where the rest simply have no value.
        const withIt = accommodations.filter((a) => a.attributes[d.id] === true).length;
        return withIt > 0 && withIt < accommodations.length;
      }
      const seen = new Set(
        accommodations
          .map((a) => a.attributes[d.id])
          .filter((v) => v !== undefined && v !== '')
          .map((v) => String(v)),
      );
      return seen.size > 1;
    })
    .slice()
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
}
